import React, { useState } from 'react';
import { readCsvFile, csvToQuestions, LETTERS } from '../../utils/qcmCsv';

// Choix "sans timer / avec timer" + durée en minutes
export function TimerField({ enabled, minutes, onChange }) {
  return (
    <div className="form-group">
      <label>Timer</label>
      <div className="qcm-timer-choice">
        <label className={`qcm-choice-pill ${!enabled ? 'active' : ''}`}>
          <input type="radio" name="timer" checked={!enabled} onChange={() => onChange({ timer_enabled: false })} />
          sans timer
        </label>
        <label className={`qcm-choice-pill ${enabled ? 'active' : ''}`}>
          <input type="radio" name="timer" checked={enabled} onChange={() => onChange({ timer_enabled: true, timer_minutes: minutes || 10 })} />
          avec timer
        </label>
        {enabled && (
          <span className="qcm-timer-minutes">
            <input type="number" min="1" max="600" value={minutes ?? ''} required
              onChange={e => onChange({ timer_minutes: e.target.value === '' ? '' : Number(e.target.value) })} />
            minutes pour tout le QCM
          </span>
        )}
      </div>
    </div>
  );
}

// Quand montrer les bonnes réponses aux visiteurs
export function FeedbackField({ value, onChange }) {
  const mode = value === 'immediate' ? 'immediate' : 'end';
  return (
    <div className="form-group">
      <label>Correction</label>
      <div className="qcm-timer-choice">
        <label className={`qcm-choice-pill ${mode === 'end' ? 'active' : ''}`}>
          <input type="radio" name="feedback" checked={mode === 'end'} onChange={() => onChange({ feedback_mode: 'end' })} />
          à la fin, avec le score
        </label>
        <label className={`qcm-choice-pill ${mode === 'immediate' ? 'active' : ''}`}>
          <input type="radio" name="feedback" checked={mode === 'immediate'} onChange={() => onChange({ feedback_mode: 'immediate' })} />
          après chaque question
        </label>
      </div>
      <p className="qcm-help">
        {mode === 'end'
          ? 'Les réponses et explications s\'affichent à la fin, avec le score final.'
          : 'Après « Vérifier », la bonne réponse et l\'explication s\'affichent, puis « Continuer » passe à la question suivante.'}
      </p>
    </div>
  );
}

// Sélection d'un fichier CSV → aperçu des questions et des erreurs ligne par ligne.
// onReady(questions, fileName) est appelé quand le fichier est lu (même s'il contient des erreurs :
// seules les lignes valides sont transmises, les erreurs sont affichées).
export function CsvQuestionsPicker({ onReady }) {
  const [result, setResult] = useState(null); // { questions, errors, fileName }
  const [readError, setReadError] = useState('');

  const onFile = async (e) => {
    const file = e.target.files[0];
    setResult(null); setReadError('');
    if (!file) { onReady([], ''); return; }
    try {
      const text = await readCsvFile(file);
      const parsed = csvToQuestions(text);
      setResult({ ...parsed, fileName: file.name });
      onReady(parsed.questions, file.name);
    } catch (err) {
      setReadError(`Lecture du fichier impossible : ${err.message}`);
      onReady([], '');
    }
  };

  return (
    <div className="form-group">
      <label>Fichier CSV</label>
      <input type="file" accept=".csv,text/csv" onChange={onFile} />
      <p className="qcm-help">
        Une ligne par question : <code>question;choix_a;choix_b;choix_c;choix_d;bonne_reponse;explication</code>.{' '}
        <a href="/exemple-qcm.csv" download>Télécharger un exemple</a>
      </p>
      {readError && <div className="flash error">{readError}</div>}
      {result && (
        <div className="qcm-csv-preview">
          {result.errors.length > 0 && (
            <div className="flash error">
              <strong>{result.errors.length} ligne(s) ignorée(s) :</strong>
              <ul>
                {result.errors.map((er, i) => <li key={i}>ligne {er.line} : {er.message}</li>)}
              </ul>
            </div>
          )}
          {result.questions.length > 0 && (
            <>
              <div className="flash">✓ {result.questions.length} question(s) prête(s) à importer depuis « {result.fileName} »</div>
              <table className="qcm-preview-table">
                <thead><tr><th>#</th><th>Question</th><th>Choix</th><th>Bonne(s) réponse(s)</th></tr></thead>
                <tbody>
                  {result.questions.map((q, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{q.text}</td>
                      <td>{q.options.length}</td>
                      <td>{q.options.map((o, j) => (o.correct ? `${LETTERS[j]}. ${o.text}` : null)).filter(Boolean).join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
